import React, { useState, useEffect } from 'react';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getCurrencySettings } from '../../../services/migration/companyDataService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

// Bank.tsx dosyasındaki BankAccount ve SubAccount arayüzleri için tip tanımları
interface BankAccount {
  id: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  subAccounts?: { [key: string]: SubAccount };
  isPosAccount?: boolean;
  isCheckAccount?: boolean;
}

interface SubAccount {
  id?: string;
  accountName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  parentId: string;
  isPosAccount?: boolean;
  isCheckAccount?: boolean;
}

// Alt hesap ekleme özellikleri
interface AddSubAccountProps {
  open: boolean;
  onClose: () => void;
  parentAccount: BankAccount;
  companyId: string | null;
  subAccountToEdit?: SubAccount | null; // Düzenlenecek alt hesap
}

// Alt hesap için veri arayüzü
interface SubAccountData {
  accountName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  parentId: string;
  isPosAccount?: boolean;
  isCheckAccount?: boolean;
}

const AddSubAccount: React.FC<AddSubAccountProps> = ({ open, onClose, parentAccount, companyId, subAccountToEdit }) => {
  const { i18n } = useTranslation('accounting');
  const isTr = i18n.language?.toLowerCase().startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  // Düzenleme modu kontrolü
  const isEditMode = !!subAccountToEdit;
  
  // Form state
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [iban, setIban] = useState('');
  const [currency, setCurrency] = useState('TRY');
  const [isPosAccount, setIsPosAccount] = useState(false);
  const [isCheckAccount, setIsCheckAccount] = useState(false);
  
  // Yükleme durumları
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Para birimi seçenekleri için state
  const [availableCurrencies, setAvailableCurrencies] = useState<{code: string, active: boolean}[]>([
    { code: 'TRY', active: true } // Varsayılan olarak TRY her zaman olacak
  ]);

  // Modal açıldığında verileri yükleme
  useEffect(() => {
    if (open && companyId) {
      loadData();
      
      // Düzenleme modu ise formu doldur
      if (subAccountToEdit) {
        populateFormForEdit(subAccountToEdit);
      }
    }
    
  }, [open, companyId, subAccountToEdit]);

  const populateFormForEdit = (subAccount: SubAccount) => {
    console.log('📝 Alt hesap düzenleme modu: Form dolduruluyor...', subAccount);
    setAccountName(subAccount.accountName || '');
    setAccountNumber(subAccount.accountNumber || '');
    setIban(subAccount.iban || '');
    setCurrency(subAccount.currency || 'TRY');
    setIsPosAccount(subAccount.isPosAccount || false);
    setIsCheckAccount(subAccount.isCheckAccount || false);
  };

  const loadData = async () => {
    if (!companyId) return;
    
    try {
      setInitialLoading(true);
      
      // Para birimlerini yükle
      loadCurrencies(companyId);
      
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      setInitialLoading(false);
      setError(tt('Veriler yüklenirken bir hata oluştu.', 'An error occurred while loading data.'));
    }
  };

  // Para birimlerini yükle (TEK SEFERLİK - Cloud Functions)
  const loadCurrencies = async (companyId: string) => {
    try {
      const currencyData = await getCurrencySettings(companyId);
      
      // Varsayılan para birimi TRY'yi hazırla
      const currencies: {code: string, active: boolean}[] = [
        { code: 'TRY', active: true } // TRY her zaman aktif olacak
      ];
      
      if (currencyData) {
        // Veritabanından gelen para birimleri
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          // TRY zaten eklendiği için tekrar eklemeye gerek yok
          if (code !== 'TRY' && active === true) {
            currencies.push({ code, active: true });
          }
        });
      }
      
      console.log('Aktif para birimleri yüklendi:', currencies);
      setAvailableCurrencies(currencies);
      
      // Eğer seçili para birimi artık mevcut değilse, TRY'ye ayarla
      const currencyExists = currencies.some(c => c.code === currency && c.active);
      
      if (!currencyExists) {
        setCurrency('TRY');
      }
    } catch (error) {
      console.error('Para birimi yükleme hatası:', error);
      // Offline fallback
      setAvailableCurrencies([{ code: 'TRY', active: true }]);
    } finally {
      setInitialLoading(false);
    }
  };

  // Form alanlarını kontrol et
  const isFormValid = () => {
    return accountName.trim() !== '' && accountNumber.trim() !== '';
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  // IBAN formatını kontrol et (Temel kontrol)
  const isValidIBAN = (iban: string): boolean => {
    // Türkiye IBAN formatı kontrol (TR + 2 kontrol rakamı + 5 banka kodu + 1 rezerve + 16 hesap no)
    const regex = /^TR\d{2}\d{5}\d{1}\d{16}$/;
    return iban.trim() === '' || regex.test(iban.replace(/\s/g, '')); // Boş olabilir ya da geçerli format
  };

  // Hesap numarasını kontrol et
  const isValidAccountNumber = (accountNumber: string): boolean => {
    // Basit bir kontrol - en az 5 rakam
    return /^\d{5,}$/.test(accountNumber.replace(/\s/g, ''));
  };

  // Kaydetme işlemi (SQL API)
  const handleSave = async () => {
    if (!isFormValid()) {
      setError(tt('Lütfen hesap adı ve hesap numarası alanlarını doldurunuz.', 'Please fill in account name and account number fields.'));
      return;
    }
    
    if (iban.trim() !== '' && !isValidIBAN(iban)) {
      setError(tt('Geçerli bir IBAN giriniz (TR ile başlayan 26 karakter).', 'Please enter a valid IBAN (26 characters starting with TR).'));
      return;
    }
    
    if (!isValidAccountNumber(accountNumber)) {
      setError(tt('Geçerli bir hesap numarası giriniz (en az 5 rakam).', 'Please enter a valid account number (at least 5 digits).'));
      return;
    }

    if (!companyId) {
      setError(tt('Şirket bilgisi bulunamadı.', 'Company information not found.'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        // Düzenleme modu
        console.log('✏️ Alt hesap güncelleniyor (SQL)...');
        
        const updatedSubAccount = {
          id: subAccountToEdit!.id,
          companyId: companyId,
          parentBankAccountId: parseInt(parentAccount.id, 10),
          accountName: accountName.trim(),
          accountNumber: accountNumber.trim(),
          iban: iban.trim() || null,
          currency,
          isPosAccount,
          isCheckAccount,
          updatedBy: ownAuthSession.getCurrentEmail() || 'System'
        };

        console.log('📤 Güncellenen veri:', updatedSubAccount);
        
        // SQL API'ye güncelleme isteği
        const response = await accountingApi.post(`${ACCOUNTING_API.BANK_ACCOUNTS}/sub-account/${subAccountToEdit!.id}/update`, updatedSubAccount);
        const result = response.data;
        console.log('✅ Alt hesap güncellendi (SQL):', result);
      } else {
        // Yeni ekleme modu
        console.log('🏦 Yeni alt banka hesabı oluşturuluyor (SQL)...');
        
        // Parent account ID'yi sayıya çevir
        const parentBankAccountId = parseInt(parentAccount.id, 10);
        if (isNaN(parentBankAccountId)) {
          throw new Error('Geçersiz ana hesap ID formatı');
        }
        
        // Alt hesap bilgilerini oluştur (SQL için)
        const newSubAccount = {
          companyId: companyId,
          parentBankAccountId: parentBankAccountId,
          accountName: accountName.trim(),
          accountNumber: accountNumber.trim(),
          iban: iban.trim() || null,
          currency,
          isPosAccount,
          isCheckAccount,
          createdBy: ownAuthSession.getCurrentEmail() || 'System'
        };

        console.log('📤 Gönderilen veri:', newSubAccount);
        
        // SQL API'ye kaydet
        const response = await accountingApi.post(ACCOUNTING_API.BANK_SUB_ACCOUNT, newSubAccount);
        const result = response.data;
        console.log('✅ Alt banka hesabı oluşturuldu (SQL):', result);
      }
      
      // Form alanlarını temizle ve modalı kapat
      resetForm();
      onClose();
    } catch (error: any) {
      console.error('❌ Alt hesap kaydedilirken hata oluştu:', error);
      setError(`${tt('Alt hesap kaydedilirken bir hata oluştu', 'An error occurred while saving sub account')}: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Form alanlarını sıfırla
  const resetForm = () => {
    setAccountName('');
    setAccountNumber('');
    setIban('');
    setCurrency('TRY');
    setIsPosAccount(false);
    setIsCheckAccount(false);
    setError(null);
  };

  // Dialog kapanma işleyicisi
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountBalanceIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            {isEditMode ? tt('Alt Hesap Düzenle', 'Edit Sub Account') : tt('Yeni Alt Hesap Ekle', 'Add New Sub Account')} - {parentAccount.bankName}
          </Typography>
          <GuideButton guideKey="finance.addSubAccount" color="inherit" size="small" />
        </Box>
        <IconButton edge="end" color="inherit" onClick={handleClose} aria-label={tt('kapat', 'close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={tt('Hesap Adı', 'Account Name')}
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                error={accountName.trim() === ''}
                helperText={accountName.trim() === '' ? tt('Hesap adı zorunludur', 'Account name is required') : ''}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={tt('Hesap Numarası', 'Account Number')}
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                error={accountNumber.trim() === '' || !isValidAccountNumber(accountNumber)}
                helperText={
                  accountNumber.trim() === '' ? tt('Hesap numarası zorunludur', 'Account number is required') : 
                  !isValidAccountNumber(accountNumber) ? tt('Geçerli bir hesap numarası giriniz', 'Please enter a valid account number') : ''
                }
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IBAN"
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                error={iban.trim() !== '' && !isValidIBAN(iban)}
                helperText={iban.trim() !== '' && !isValidIBAN(iban) ? tt('Geçerli bir IBAN giriniz (TR ile başlayan 26 karakter)', 'Please enter a valid IBAN (26 characters starting with TR)') : ''}
                size="small"
                placeholder="TR00 0000 0000 0000 0000 0000 00"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('Para Birimi', 'Currency')}</InputLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  label={tt('Para Birimi', 'Currency')}
                >
                  {availableCurrencies
                    .filter(currency => currency.active)
                    .map(currency => (
                      <MenuItem key={currency.code} value={currency.code}>
                        {`${currency.code} (${getCurrencySymbol(currency.code)})`}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isPosAccount}
                      onChange={(e) => setIsPosAccount(e.target.checked)}
                    />
                  }
                  label={tt('POS Hesabı', 'POS Account')}
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isCheckAccount}
                      onChange={(e) => setIsCheckAccount(e.target.checked)}
                    />
                  }
                  label={tt('Çek Hesabı', 'Check Account')}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                {tt('Bu hesap,', 'This account will be added as a sub account of')} "{parentAccount.bankName}" {tt('adlı ana banka hesabının alt hesabı olarak eklenecektir.', 'main bank account.')}
              </Alert>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" size="small">{tt('İptal', 'Cancel')}</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          size="small"
          disabled={loading || !isFormValid()}
        >
          {loading ? (
            <>
              <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
              {tt('Kaydediliyor...', 'Saving...')}
            </>
          ) : (
            isEditMode ? tt('Güncelle', 'Update') : tt('Kaydet', 'Save')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSubAccount; 